import { SOURCE_PLATFORM_TYPE } from '@/entities/public-filter';
import {
  SCRAPING_INTENT_MODE,
  SCRAPING_INTENT_SELECTOR_TYPE,
  SCRAPING_INTENT_STATUS,
  type ScrapingIntentSummary,
} from '../types';
import { formatScrapingDate, toScrapingRunViewModel } from './scraping-run-view-model';

export type ScrapingIntentKind = 'google-search' | 'direct-url';

export function getScrapingIntentKind(intent: ScrapingIntentSummary): ScrapingIntentKind | null {
  if (intent.payload.selector.type === SCRAPING_INTENT_SELECTOR_TYPE.KEYWORD) {
    return 'google-search';
  }

  if (intent.payload.selector.type === SCRAPING_INTENT_SELECTOR_TYPE.URLS) {
    return 'direct-url';
  }

  return null;
}

export function getScrapingPlatformLabel(platform: SOURCE_PLATFORM_TYPE) {
  switch (platform) {
    case SOURCE_PLATFORM_TYPE.TIKTOK:
      return 'TikTok';
    case SOURCE_PLATFORM_TYPE.INSTAGRAM:
      return 'Instagram';
    case SOURCE_PLATFORM_TYPE.YOUTUBE:
      return 'YouTube';
    default:
      return platform;
  }
}

export function getScrapingIntentModeLabel(mode: SCRAPING_INTENT_MODE) {
  return mode === SCRAPING_INTENT_MODE.ONETIME ? 'One-time' : 'Periodic';
}

export function getScrapingIntentStatusLabel(status: SCRAPING_INTENT_STATUS) {
  return status === SCRAPING_INTENT_STATUS.ACTIVE ? 'Active' : 'Inactive';
}

export function toScrapingIntentViewModel(intent: ScrapingIntentSummary) {
  const selector = intent.payload.selector;
  const limit = intent.payload.limit;
  const dateRange = limit?.date_range;
  const target =
    selector.type === SCRAPING_INTENT_SELECTOR_TYPE.KEYWORD
      ? (selector.keyword ?? '-')
      : `${selector.urls?.length ?? 0} URLs`;

  return {
    id: intent.intent_id,
    name: intent.name ?? target,
    target,
    targetDescription:
      selector.type === SCRAPING_INTENT_SELECTOR_TYPE.KEYWORD
        ? selector.exact_match
          ? 'Exact keyword match'
          : 'Keyword match'
        : (selector.urls ?? []).slice(0, 2).join(', '),
    platform: getScrapingPlatformLabel(intent.payload.platform),
    mode: getScrapingIntentModeLabel(intent.mode),
    status: getScrapingIntentStatusLabel(intent.status),
    statusTone:
      intent.status === SCRAPING_INTENT_STATUS.ACTIVE ? ('success' as const) : ('neutral' as const),
    itemPerDay: limit?.item_per_day.toLocaleString() ?? '-',
    dateRange:
      dateRange == null
        ? '-'
        : `${formatScrapingDate(dateRange.start_date)} - ${formatScrapingDate(dateRange.end_date)}`,
    createdAt: formatScrapingDate(intent.created_at),
    stoppedAt: formatScrapingDate(intent.stopped_at),
    latestRun: intent.latest_run == null ? null : toScrapingRunViewModel(intent.latest_run),
  };
}
